import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { licenzeApi, formatDate } from '../../../services/licenzeApi';
import { LicenzeStats, LicenzaSimple } from '../../../types/licenze';
import './LicenzePanel.css';

const LicenzePanel: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'scadenza' | 'scadute'>('scadenza');
  const [stats, setStats] = useState<LicenzeStats | null>(null);
  const [licenzeInScadenza, setLicenzeInScadenza] = useState<LicenzaSimple[]>([]);
  const [licenzeScadute, setLicenzeScadute] = useState<LicenzaSimple[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, licenzeScadenza, licenzeScadute] = await Promise.all([
        licenzeApi.getStats(),
        licenzeApi.getLicenzeInScadenza(30),
        licenzeApi.getLicenzeScadute(30)
      ]);

      setStats(statsData);
      setLicenzeInScadenza(licenzeScadenza);
      setLicenzeScadute(licenzeScadute);
    } catch (error: any) {
      console.error('Errore nel caricamento dati licenze:', error);
      setError(error.message || 'Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllLicenze = () => {
    navigate('/licenze');
  };

  const formatScadenza = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  if (loading) {
    return (
      <div className="licenze-panel">
        <div className="panel-header">
          <h3>📄 Licenze</h3>
        </div>
        <div className="panel-content">
          <div className="loading">Caricamento licenze...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="licenze-panel">
        <div className="panel-header">
          <h3>📄 Licenze</h3>
        </div>
        <div className="panel-content">
          <div className="error-message">
            {error}
            <button onClick={loadData} className="retry-button">
              Riprova
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="licenze-panel">
      <div className="panel-header">
        <h3>📄 Licenze</h3>
        <button 
          className="view-all-btn" 
          onClick={handleViewAllLicenze}
          title="Apri la sezione completa delle licenze"
        >
          Dettagli →
        </button>
      </div>

      <div className="panel-content">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'scadenza' ? 'active' : ''}`}
            onClick={() => setActiveTab('scadenza')}
            title="Licenze che scadranno nei prossimi 30 giorni"
          >
            In Scadenza
          </button>
          <button 
            className={`tab-btn ${activeTab === 'scadute' ? 'active' : ''}`}
            onClick={() => setActiveTab('scadute')}
            title="Licenze scadute negli ultimi 30 giorni"
          >
            Scadute
          </button>
        </div>

        {/* Licenze Table */}
        <div className="licenze-table-container">
          <table className="licenze-table">
            <thead>
              <tr>
                <th className="col-date">Data Scad.</th>
                <th className="col-license">Licenza</th>
                <th className="col-username">Username</th>
              </tr>
            </thead>
            <tbody>
              {activeTab === 'scadenza' ? (
                licenzeInScadenza.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="no-data">
                      ✅ Nessuna licenza in scadenza
                    </td>
                  </tr>
                ) : (
                  licenzeInScadenza.map((licenza, index) => (
                    <tr key={index}>
                      <td className="date-cell">
                        <span className="date-icon">🔍</span>
                        {formatScadenza(licenza.dataScadenzaLicenza)}
                      </td>
                      <td className="license-cell">
                        <span className="license-name">{licenza.licenza}</span>
                      </td>
                      <td className="username-cell">
                        {licenza.nomeCompleto || licenza.username}
                      </td>
                    </tr>
                  ))
                )
              ) : (
                licenzeScadute.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="no-data">
                      ✅ Nessuna licenza scaduta
                    </td>
                  </tr>
                ) : (
                  licenzeScadute.map((licenza, index) => (
                    <tr key={index} className="scaduta-row">
                      <td className="date-cell">
                        <span className="date-icon">🔍</span>
                        {formatScadenza(licenza.dataScadenzaLicenza)}
                      </td>
                      <td className="license-cell">
                        <span className="license-name">{licenza.licenza}</span>
                      </td>
                      <td className="username-cell">
                        {licenza.nomeCompleto || licenza.username}
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>

        {/* Stats Footer */}
        {stats && (
          <div className="stats-footer">
            <div className="stat-summary">
              <span className="stat-item">
                <strong>{stats.totaleLicenze}</strong> Totali
              </span>
              <span className="stat-item active">
                <strong>{stats.licenzeAttive}</strong> Attive
              </span>
              <span className="stat-item warning">
                <strong>{stats.licenzeInScadenza}</strong> In Scadenza
              </span>
              <span className="stat-item danger">
                <strong>{stats.licenzeScadute}</strong> Scadute
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenzePanel;